import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import type { ComponentProps } from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import type { Rarity } from '@sorare/core/src/__generated__/globalTypes';
import { Horizontal, Vertical } from '@sorare/core/src/atoms/layout/flex';
import { BodyS, LabelM } from '@sorare/core/src/atoms/typography';
import { fantasy } from '@sorare/core/src/lib/glossary';
import { isTypeFn } from '@sorare/core/src/lib/gql';
import type { HexColor } from '@sorare/core/src/style/types';

import { TargetIcon } from 'assets/icons/TargetIcon';
import { ClickableAnyRewardConfig } from 'components/rewards/ClickableAnyRewardConfig';

import { RewardRange, RewardRangeVariant } from '../RewardRange';
import type {
  ThresholdRewards_so5League,
  ThresholdRewards_so5RewardConfig,
} from './__generated__/index.graphql';

export const targetColors: HexColor[] = [
  'var(--c-score-high)',
  'var(--c-score-mediumHigh)',
  'var(--c-score-medium)',
];

const StartHorizontal = styled(Horizontal)`
  align-items: flex-start;
`;

type Props = {
  so5RewardConfigs: ThresholdRewards_so5RewardConfig[];
  defaultRarity: Rarity | null;
  so5League: ThresholdRewards_so5League;
  hideCompletionStatus?: boolean;
} & Pick<ComponentProps<typeof RewardRange>, 'withSeparator'>;

export const ThresholdRewards = ({
  so5RewardConfigs,
  defaultRarity,
  so5League,
  withSeparator,
}: Props) => {
  const sortedSo5RewardConfigs = so5RewardConfigs
    .filter(scoreReward => scoreReward.score)
    .sort((a, b) => (b.score || 0) - (a.score || 0));

  if (!sortedSo5RewardConfigs.length) {
    return null;
  }

  return (
    <Vertical gap={2}>
      {sortedSo5RewardConfigs.map((so5RewardConfig, index) => {
        const targetNb = index + 1;

        const monetaryRewardConfig = so5RewardConfig.rewardConfigs.find(
          isTypeFn('MonetaryRewardConfig')
        );
        const conversionCreditRewardConfig = so5RewardConfig.rewardConfigs.find(
          isTypeFn('ConversionCreditRewardConfig')
        );

        return (
          <RewardRange
            key={so5RewardConfig.score}
            variant={RewardRangeVariant.ROW}
            title={
              <StartHorizontal>
                <TargetIcon color={targetColors[index]} />
                <Vertical gap={0.5}>
                  <LabelM>
                    <FormattedMessage
                      {...(sortedSo5RewardConfigs.length === 1
                        ? fantasy.target
                        : fantasy.targetNumber)}
                      values={{ targetNb }}
                    />{' '}
                    (
                    <FormattedMessage
                      {...fantasy.targetPoints}
                      values={{ target: so5RewardConfig.score }}
                    />
                    )
                  </LabelM>
                  {!!(monetaryRewardConfig || conversionCreditRewardConfig) && (
                    <BodyS color="var(--c-nd-600)">
                      {monetaryRewardConfig?.sharedPool ||
                      conversionCreditRewardConfig?.sharedPool ? (
                        <FormattedMessage
                          id="ThresholdRewards.thePotIsSplitEvenly"
                          defaultMessage="The pot is split evenly"
                        />
                      ) : (
                        <FormattedMessage
                          id="ThresholdRewards.notSharedPool2"
                          defaultMessage="Prize is per lineup"
                        />
                      )}
                    </BodyS>
                  )}
                </Vertical>
              </StartHorizontal>
            }
            rewards={so5RewardConfig.rewardConfigs.map((rewardConfig, i) => (
              <ClickableAnyRewardConfig
                // eslint-disable-next-line react/no-array-index-key
                key={i}
                anyRewardConfig={rewardConfig}
                rarity={defaultRarity || undefined}
                so5League={so5League}
              />
            ))}
            withSeparator={withSeparator}
          />
        );
      })}
    </Vertical>
  );
};

ThresholdRewards.fragments = {
  so5RewardConfig: gql`
    fragment ThresholdRewards_so5RewardConfig on So5RewardConfig {
      id
      score
      rewardConfigs {
        id
        ... on MonetaryRewardConfig {
          id
          sharedPool
        }
        ... on ConversionCreditRewardConfig {
          id
          sharedPool
        }
        ...ClickableAnyRewardConfig_anyRewardConfigInterface
      }
    }
    ${ClickableAnyRewardConfig.fragments.anyRewardConfigInterface}
  ` as TypedDocumentNode<ThresholdRewards_so5RewardConfig>,
  so5League: gql`
    fragment ThresholdRewards_so5League on So5League {
      slug
      so5Fixture {
        slug
        aasmState
      }
      ...ClickableAnyRewardConfig_so5League
    }
    ${ClickableAnyRewardConfig.fragments.so5League}
  ` as TypedDocumentNode<ThresholdRewards_so5League>,
};
